// functions/src/review 응답 형식
export type Verdict = 'default' | 'search' | 'both' | 'discard' | 'rework';
export type QueueName = 'pending' | 'rework' | 'approved' | 'discarded' | 'published';

export interface ReviewAsset {
  assetId: string;
  vocabIds: string[];
  stage: string;
  parentAssetId: string | null;
  createdAt: string;
  generation: { modelId: string; costKrw: number | null; prompt?: string; promptParts?: { subject: string | null; extra: string | null } | null } | null;
  origin?: 'auto' | 'manual' | 'upload' | 'existing';
  /** 앱 기존 이미지(#75) */
  existing?: { kind: 'card' | 'search' | 'unused'; ref: string; r2Key: string; url: string } | null;
  original?: { key: string } | null;
  postprocess: { state: string; reasons: string[]; steps?: { type: string; detail?: string }[]; final?: { key?: string; hasAlpha?: boolean } | null } | null;
  review: { verdict: Verdict | null; by: string | null; at: string | null; note: string | null; recognizableAt3cm: boolean | null; history: { verdict: Verdict; by: string; at: string; note: string | null }[] };
  originalUrl: string | null;
  finalUrl: string | null;
  publish?: { default: unknown | null; search: SearchPublishState | null };
}

export interface PublishStep {
  state: 'pending' | 'running' | 'succeeded' | 'failed';
  at: string | null;
  error: string | null;
}

export interface SearchPublishState {
  state: 'pending' | 'running' | 'succeeded' | 'failed' | 'partial';
  searchImageId: string;
  path: string;
  steps: { upload: PublishStep; metadata: PublishStep; link: PublishStep };
  attempts: number;
}

export interface PublishResult {
  assetId: string;
  state: string;
  searchImageId: string;
  url?: string;
  failedStep?: string;
  error?: string;
  already?: boolean;
}

export const STEP_LABEL = { upload: '운영 CDN 업로드', metadata: '검색 메타데이터', link: '어휘 연결' } as const;

export const cdnUrl = (p: SearchPublishState) => `https://cdn.isori.app/images/${p.path}/${p.searchImageId}.webp`;

/** 검색용 게시 버튼을 보일지 */
export function canPublishSearch(a: ReviewAsset): boolean {
  return (a.review.verdict === 'search' || a.review.verdict === 'both') && !!a.finalUrl && a.publish?.search?.state !== 'succeeded';
}

export interface ReviewQueue {
  assets: ReviewAsset[];
  vocab: { vocabId: string; text: string; category: string; slug: string | null; keywords?: string[] }[];
}

export interface VerdictResult {
  results: { assetId: string; ok: boolean; stage?: string; error?: string }[];
  okCount: number;
}

export const QUEUE_LABEL: Record<QueueName, string> = { pending: '검수 대기', rework: '재작업', approved: '승인(미게시)', discarded: '폐기', published: '게시 완료' };

export const VERDICT_LABEL: Record<Verdict, string> = { default: '기본', search: '검색용', both: '둘 다', discard: '폐기', rework: '재작업' };

/** 판정을 바꿀 수 있는 단계(admin-model applyReview와 같음) */
export const REVIEWABLE_STAGES = new Set(['in_review', 'rework', 'approved', 'discarded']);

export const REWORK_PRESETS = ['피사체 확대', '단순화(불필요한 요소 제거)', '재생성', '한국 생활환경에 맞지 않음', '3cm에서 알아보기 어려움', '특정 브랜드 연상'];

/** 어휘가 연결되지 않은 자산 묶음 키(미사용 운영 이미지) */
export const UNASSIGNED = '-';

export const EXISTING_KIND_LABEL: Record<'card' | 'search' | 'unused', string> = { card: '앱 기본 카드', search: '앱 검색 이미지', unused: '미사용 운영 이미지' };

/** 앱 기존 이미지에 어휘를 연결할 수 있는지(게시 중·게시됨 제외) */
export const canAssign = (a: Pick<ReviewAsset, 'origin' | 'stage'>) => a.origin === 'existing' && !['publishing', 'published', 'publish_failed'].includes(a.stage);

/** 후처리를 다시 할 수 없는 단계(게시 중·게시됨·처리 중) */
export const REPROCESS_BLOCKED = new Set(['publishing', 'published', 'publish_failed', 'generating', 'postprocessing']);

/** 흰 배경 규격(G-3, 2026-11-01) 이전에 만든 최종본(투명 배경) */
export const isLegacyTransparent = (a: Pick<ReviewAsset, 'postprocess'>) =>
  !!a.postprocess?.final && (a.postprocess.final.hasAlpha === true || !!a.postprocess.steps?.some((s) => s.type === 'remove-background'));

/** 배경을 판정할 수 없어 512 전체를 쓴 최종본(가득 찬 피사체·장면, 2026-11-02) */
export const isFullBleed = (a: Pick<ReviewAsset, 'postprocess'>) => !!a.postprocess?.steps?.some((s) => s.type === 'white-background' && !!s.detail?.startsWith('배경 없음'));

export const canReprocess = (a: Pick<ReviewAsset, 'stage' | 'originalUrl'>) => !!a.originalUrl && !REPROCESS_BLOCKED.has(a.stage);

/** 같은 어휘 후보끼리 묶는다(어휘 순서는 가장 최근 후보 기준) */
export function groupByVocab(assets: readonly ReviewAsset[]) {
  const groups = new Map<string, ReviewAsset[]>();
  for (const a of assets) {
    const v = a.vocabIds[0] ?? '-';
    groups.set(v, [...(groups.get(v) ?? []), a]);
  }
  return [...groups.entries()].map(([vocabId, items]) => ({ vocabId, items }));
}

/** 연속 검수 단축키(R1 §10.2) */
export type ShortcutAction = Verdict | 'next' | 'prev' | 'toggleSource';
export const SHORTCUTS: Record<string, ShortcutAction> = {
  '1': 'default',
  '2': 'search',
  '3': 'both',
  r: 'rework',
  R: 'rework',
  d: 'discard',
  D: 'discard',
  Delete: 'discard',
  ArrowRight: 'next',
  j: 'next',
  ArrowLeft: 'prev',
  k: 'prev',
  s: 'toggleSource',
  S: 'toggleSource',
};
/** 한글 자판(두벌식)에서 같은 자리의 자모. 한/영 전환 없이도 같은 키로 동작한다 */
export const HANGUL_SHORTCUTS: Record<string, ShortcutAction> = {
  ㄱ: 'rework',
  ㄲ: 'rework',
  ㅇ: 'discard',
  ㄴ: 'toggleSource',
  ㅓ: 'next',
  ㅏ: 'prev',
};

/** 자판 배열과 무관한 물리 키(KeyboardEvent.code). 한글 입력기가 조합 중이라 key가 'Process'로 오는 경우 */
export const CODE_SHORTCUTS: Record<string, ShortcutAction> = {
  Digit1: 'default',
  Digit2: 'search',
  Digit3: 'both',
  Numpad1: 'default',
  Numpad2: 'search',
  Numpad3: 'both',
  KeyR: 'rework',
  KeyD: 'discard',
  KeyS: 'toggleSource',
  KeyJ: 'next',
  KeyK: 'prev',
};

export const SHORTCUT_HELP = '1 기본 · 2 검색용 · 3 둘 다 · R(ㄱ) 재작업 · D(ㅇ) 폐기 · ←/→ 이전/다음 · S(ㄴ) 원본/최종 · 한글 자판 그대로 됩니다';

/** 입력 중이거나 조합키를 누른 경우는 단축키로 처리하지 않는다. 영문·한글 자판 모두 같은 자리의 키로 동작한다 */
export function shortcutFor(e: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'altKey' | 'target'> & { code?: string }): ShortcutAction | null {
  if (e.ctrlKey || e.metaKey || e.altKey) return null;
  const t = e.target as HTMLElement | null;
  if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return null;
  return SHORTCUTS[e.key] ?? HANGUL_SHORTCUTS[e.key] ?? (e.code ? CODE_SHORTCUTS[e.code] : undefined) ?? null;
}
